import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  Tooltip,
  IconButton,
  Button,
  Chip,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ClearIcon from "@mui/icons-material/Clear";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import FilterBar from "../../../../../components/FilterNSearchBar/FilterNSearchBar";
import { GET_EVIDENCE_REQUESTS, CREATE_EVIDENCE_REQUEST, UPDATE_EVIDENCE_REQUEST } from "../../../../../apis/settingsApis/audit-apis/evidenceRequestApis";
import { GET_USER_DETAILS } from "../../../../../apis/dashboard-apis/dashboardDetailApi";
import { GET_AUDIT_FRAMEWORK_MIDDLE_PANEL } from "../../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_EXTERNAL_USERS } from "../../../../../apis/organization-apis/people-apis/externalUserApis";
import { formatDate } from "../../../../../utils/dateUtils";
import { AuditChip } from "../common/AuditChip";
import EvidenceRequestDialog from "../Dialogs/EvidenceRequestDialog";
import EvidenceRequestDetailsDialog from "../Dialogs/EvidenceRequestDetailsDialog";
import { AuditorStatusType, EVIDENCE_REQUEST_STATUS, AuditStatus } from "../../../../../utils/enums";
import EvidenceRequestStatusFilterPopover from "../Filters/EvidenceRequestStatusFilterPopover";
import RoleFilterPopover from "../../../../Evidence/components/common/RoleFilterPopover";

const EvidenceRequestsTab = ({ audit, isAuditorView = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [controlMap, setControlMap] = useState(new Map());
  const [controlsList, setControlsList] = useState([]);
  const [authorMap, setAuthorMap] = useState(new Map());
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRequestForDetails, setSelectedRequestForDetails] = useState(null);

  // Get user details
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const userDetails = userData?.getAccountDetails?.userDetails || {};
  const userId = userDetails?.user_id;
  const userType = userDetails?.user_type === "internal" ? "admin" : "auditor";
  const employeeDataId = userDetails?.employee_data_id;

  const { data: employeesData } = useQuery(GET_STAFF_DETAILS, {
    variables: { filter: {}, audit_id: audit?.id },
    fetchPolicy: "cache-first",
    skip: !audit?.id,
  });
  const { data: externalUsersData } = useQuery(GET_EXTERNAL_USERS, { variables: { page: 1, limit: 1000 }, fetchPolicy: "cache-first" });

  useEffect(() => {
    const map = new Map();
    employeesData?.getEmployees?.data?.forEach(emp => {
      if (emp.employee_data_id) map.set(String(emp.employee_data_id), { name: emp.name, email: emp.work_email });
    });
    externalUsersData?.getExternalUsers?.data?.forEach(user => {
      if (user.id) map.set(String(user.id), { name: user.name, email: user.email });
    });
    setAuthorMap(map);
  }, [employeesData, externalUsersData]);

  // Fetch audit framework middle panel
  const { data: frameworkData } = useQuery(GET_AUDIT_FRAMEWORK_MIDDLE_PANEL, {
    variables: {
      audit_id: audit?.id,
    },
    skip: !audit?.id || !audit?.audit_start_date,
    fetchPolicy: "cache-first",
  });

  // Build control map for lookup and list for dropdown
  useEffect(() => {
    if (!frameworkData?.getAuditFrameworkMiddlePanel?.areaDetails) return;

    const map = new Map();
    const list = [];

    // Flatten the hierarchical structure to extract controls
    frameworkData.getAuditFrameworkMiddlePanel.areaDetails.forEach((area) => {
      (area.criterias || []).forEach((criteria) => {
        (criteria.controls || []).forEach((control) => {
          if (!control || control.controlsId == null) return;

          if (map.has(control.controlsId)) {
            return;
          }

          const controlData = {
            id: control.auditControlId,
            auditControlId: control.auditControlId,
            controlsId: control.controlsId,
            controlsName: control.controlsName || "",
            controlsTitle: control.controlsTitle || "",
            controlsDescription: control.controlsDescription || "",
            status: control.status,
          };

          map.set(control.controlsId, controlData);
          
          if (control.status !== AuditorStatusType.APPROVED) {
            list.push(controlData);
          }
        });
      });
    });

    list.sort((a, b) => (a.controlsId || 0) - (b.controlsId || 0));
    setControlMap(map);
    setControlsList(list);
  }, [frameworkData]);

  // Fetch evidence requests - get all for the audit (only controls)
  const { data: requestsData, loading, refetch } = useQuery(GET_EVIDENCE_REQUESTS, {
    variables: {
      filter: {
        audit_id: audit?.id,
        // Removed entity_type filter to fetch both CONTROL and CHECK requests
      },
      page: 1,
      limit: 1000,
    },
    skip: !audit?.id,
    fetchPolicy: "cache-and-network",
  });

  const evidenceRequests = requestsData?.getEvidenceRequests?.data || [];

  // Filter to only show control requests
  const controlRequests = useMemo(() => {
    return evidenceRequests.filter((request) => request.entity_type === "control");
  }, [evidenceRequests]);

  const roleFilterOptions = useMemo(() => {
    const set = new Set();
    controlRequests.forEach((r) => {
      if (r?.author_type) set.add(r.author_type);
    });
    return Array.from(set);
  }, [controlRequests]);

  useEffect(() => {
    if (!searchQuery.trim() && statusFilter.length === 0 && roleFilter.length === 0) {
      setFilteredRequests(controlRequests);
      return;
    }

    const query = searchQuery.trim().toLowerCase();
    const filtered = controlRequests.filter((request) => {
      // Status filter
      if (statusFilter.length > 0 && request.status && !statusFilter.includes(request.status)) {
        return false;
      }

      // Role filter
      if (roleFilter.length > 0 && request.author_type && !roleFilter.includes(request.author_type)) {
        return false;
      }

      if (!query) {
        return true;
      }

      const controlsId = request.control_id;
      const control = controlsId ? controlMap.get(controlsId) : null;

      return (
        request.evidence_requested_reason?.toLowerCase().includes(query) ||
        request.author_name?.toLowerCase().includes(query) ||
        request.author_email?.toLowerCase().includes(query) ||
        control?.controlsName?.toLowerCase().includes(query) ||
        (controlsId != null && controlsId.toString().includes(query)) ||
        request.entity_id?.toString().includes(query)
      );
    });

    setFilteredRequests(filtered);
  }, [searchQuery, statusFilter, roleFilter, controlRequests, controlMap]);

  const [createRequest, { loading: creating }] = useMutation(CREATE_EVIDENCE_REQUEST, {
    onCompleted: () => {
      setDialogOpen(false);
      setSelectedRequest(null);
      refetch();
    },
    onError: (error) => {
      console.error("Error creating evidence request:", error);
    },
  });

  const [updateRequest, { loading: updating }] = useMutation(UPDATE_EVIDENCE_REQUEST, {
    onCompleted: () => {
      setDialogOpen(false);
      setSelectedRequest(null);
      setDetailsDialogOpen(false);
      setSelectedRequestForDetails(null);
      refetch();
    },
    onError: (error) => {
      console.error("Error updating evidence request:", error);
    },
  });

  const handleCreateClick = () => {
    setDialogMode("create");
    setSelectedRequest(null);
    setDialogOpen(true);
  };

  const handleDialogSubmit = async (data) => {
    if (dialogMode === "create") {
      await createRequest({
        variables: {
          input: {
            audit_id: audit.id,
            entity_type: data.entity_type,
            entity_id: data.entity_id,
            evidence_requested_reason: data.evidence_requested_reason,
          },
        },
      });
    } else if (dialogMode === "edit") {
      await updateRequest({
        variables: {
          input: [
            {
              id: data.id,
              audit_id: audit?.id,
              evidence_requested_reason: data.evidence_requested_reason,
            },
          ],
        },
      });
    }
  };


  const handleViewDetails = (request) => {
    setSelectedRequestForDetails(request);
    setDetailsDialogOpen(true);
  };

  const handleDetailsDialogClose = () => {
    setDetailsDialogOpen(false);
    setSelectedRequestForDetails(null);
  };

  const handleSaveFromDetails = async (data) => {
    if (selectedRequestForDetails) {
      await updateRequest({
        variables: {
          input: [
            {
              id: data.id,
              audit_id: audit?.id,
              evidence_requested_reason: data.evidence_requested_reason,
            },
          ],
        },
      });
    }
  };

  const isRequestOwner = (request) => {
    if (userType === "admin") {
      return String(request.author_id) === String(employeeDataId);
    } else {
      return String(request.author_id) === String(userId);
    }
  };

  const columns = [
    { label: "Control", field: "entity", align: "left", width: "20%" },
    { label: "Description", field: "reason", align: "left", width: "18%" },
    { label: "Requested By", field: "author", align: "left", width: "15%" },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          Role
          <RoleFilterPopover
            options={roleFilterOptions}
            activeFilters={roleFilter}
            onFilterChange={setRoleFilter}
          />
        </Box>
      ),
      field: "authorRole",
      align: "left",
      width: "15%",
    },
    {
      label: (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          Status
          <EvidenceRequestStatusFilterPopover
            activeFilters={statusFilter}
            onFilterChange={setStatusFilter}
          />
        </Box>
      ),
      field: "status",
      align: "left",
      width: "15%",
    },
    { label: "Date", field: "date", align: "left", width: "10%" },
    { label: "", field: "actions", align: "left", width: "5%" },
  ];

  const renderCell = (row, column) => {
    const controlsId = row.control_id;
    const control = controlsId ? controlMap.get(controlsId) : null;

    switch (column.field) {
      case "entity":
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                fontWeight: 500,
              }}
            >
              {control?.controlsName || (controlsId ? `Control ${controlsId}` : "Control")}
            </Typography>
            {control?.controlsTitle && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.customColors.text.secondary,
                  fontSize: "0.75rem",
                }}
              >
                {control.controlsTitle}
              </Typography>
            )}
          </Box>
        );
      case "reason":
        return (
          <Box sx={{ py: 0.5 }}>
            <Tooltip title={row.evidence_requested_reason || "-"} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  pl:0.45,
                  fontSize: "0.875rem",
                  lineHeight: 1.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  width: "100%",
                  color: theme.customColors.text.strong,
                }}
              >
                {row.evidence_requested_reason || "-"}
              </Typography>
            </Tooltip>
          </Box>
        );
      case "author": {
        const author = authorMap.get(String(row.author_id));
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                pl:0.65,
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
                fontWeight: 500,
              }}
            >
              {author?.name || row.author_name || "-"}
            </Typography>
          </Box>
        );
      }
      case "authorRole":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:0.5 }}>
            <AuditChip author_type={row.author_type} />
          </Box>
        );
      case "status": {
        const isClosed = row.status === EVIDENCE_REQUEST_STATUS.KEY.CLOSE;
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:1 }}>
            <Chip
              label={isClosed ? "Closed" : "Open"}
              sx={{
                backgroundColor: isClosed
                  ? theme.customColors.status.error.bg
                  : theme.customColors.status.success.bg,
                color: isClosed
                  ? theme.customColors.status.error.text
                  : theme.customColors.status.success.text,
                minWidth: "90px",
                textAlign: "center",
                borderRadius: 0,
                fontWeight: 600,
                fontSize: "0.75rem",
                border: "none",
              }}
            />
          </Box>
        );
      }
      case "date":
        return (
          <Box sx={{ textAlign: "left", width: "100%", pl:1 }}>
            <Typography
              variant="body2"
              sx={{
                textAlign: "left",
                width: "100%",
                color: theme.customColors.text.strong,
              }}
            >
              {row.rego_valid_from ? formatDate(row.rego_valid_from) : "-"}
            </Typography>
          </Box>
        );
      case "actions":
        return (
          <Box sx={{ textAlign: "center", width: "100%" }}>
            <Tooltip title="View Details" arrow>
              <IconButton
                size="small"
                onClick={() => handleViewDetails(row)}
                sx={{
                  color: theme.customColors.icons.dark,
                  "&:hover": {
                    backgroundColor: theme.customColors.background.light,
                    transform: "translateX(2px)",
                    transition: "all 0.2s ease",
                  },
                }}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter([]);
    setRoleFilter([]);
  };

  const isAnyFilterActive =
    Boolean(searchQuery.trim()) || statusFilter.length > 0 || roleFilter.length > 0;

  return (
    <Box>
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="h6"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Evidence Requests
              </Typography>
              {isAnyFilterActive && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: "18px",
              fontWeight: "bold",
            },
            padding: "14px",
          }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {isAnyFilterActive && (
                <Tooltip title="Clear all filters" arrow>
                  <IconButton
                    size="small"
                    onClick={clearFilters}
                    sx={{
                      color: "#6B7280",
                      backgroundColor: "#F3F4F6",
                      "&:hover": {
                        backgroundColor: "#E5E7EB",
                        color: "#374151",
                      },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={
                  audit?.audit_status === AuditStatus.COMPLETED
                    ? "New Request is disabled because the audit has been completed"
                    : audit?.audit_status === AuditStatus.ARCHIVED
                      ? "New Request is disabled because the audit has been archived"
                      : "Add a new evidence request"
                }
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={handleCreateClick}
                    disabled={
                      audit?.audit_status === AuditStatus.COMPLETED ||
                      audit?.audit_status === AuditStatus.ARCHIVED
                    }
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": { bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary },
                      "&:disabled": {
                        bgcolor: theme.customColors.background.light,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    New Request
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              filterLabel=""
              filters={[]}
              selectedFilter={null}
              onFilterChange={() => {}}
              onClearFilters={clearFilters}
              countLabel={`${filteredRequests.length} request${
                filteredRequests.length !== 1 ? "s" : ""
              } found`}
            />
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          {loading ? (
            <TableSkeleton />
          ) : (
            <Box>
              <ReusableTable
                columns={columns}
                data={filteredRequests}
                renderCell={renderCell}
                loading={loading}
                height="auto"
                showPagination={true}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Evidence Request Dialog */}
      <EvidenceRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        mode={dialogMode}
        initialData={selectedRequest}
        loading={creating || updating}
        controlsList={controlsList}
      />

      {/* Evidence Request Details Dialog */}
      {selectedRequestForDetails && (() => {
        const controlsId = selectedRequestForDetails.control_id;
        const control = controlsId ? controlMap.get(controlsId) : null;
        
        return (
          <EvidenceRequestDetailsDialog
            open={detailsDialogOpen}
            onClose={handleDetailsDialogClose}
            request={selectedRequestForDetails}
            control={control}
            author={authorMap.get(String(selectedRequestForDetails.author_id))}
            onSave={handleSaveFromDetails}
            isOwner={isRequestOwner(selectedRequestForDetails)}
            isAuditor={isAuditorView}
            saving={updating}
            audit={audit}
            onRefresh={refetch}
          />
        );
      })()}
    </Box>
  );
};

export default EvidenceRequestsTab;
